/**
 * PSP/CRE Tool v2.0 - Evacuation Asset Allocation Constants
 * 
 * Purpose: Define organic asset allocation by unit type and echelon level
 * for medical evacuation planning. Supports NATO doctrine where assets
 * are organic to units but can be attached/detached as needed.
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: December 30, 2024
 * Version: 1.0.0 (EA-001, EA-002 Implementation)
 * 
 * References:
 * - NATO AJMedP-1 (Allied Joint Medical Planning Doctrine)
 * - ATP 4-02.2 (Medical Evacuation)
 * - Romanian Army Medical Doctrine
 * - US Army FM 4-02 (Army Health System)
 * 
 * Architecture:
 * - organic_assets: Permanently assigned to unit (TOE/MTOE)
 * - attached_assets: Temporarily received from another unit
 * - detached_assets: Temporarily sent to support another unit  
 * - effective_assets: Calculated (organic + attached - detached)
 * 
 * Usage:
 * import { 
 *   getRole1OrganicAssets,
 *   getRole2BasicOrganicAssets,
 *   createEmptyAssetBlock,
 *   calculateEffectiveAssets
 * } from '../constants/evacuationAssetAllocation';
 */

// ============================================================================
// ROLE 1 (BATTALION LEVEL) ORGANIC ASSETS BY PROFILE
// ============================================================================

/**
 * Role 1 organic assets vary by battalion type
 * Combat battalions have more assets than support battalions
 */
export const ROLE1_ORGANIC_ASSETS = {
  // Standard combat battalion (Infantry, Mechanized, Cavalry)
  STANDARD: {
    ground_ambulances: {
      wheeled: {
        count: 4,
        type: 'TABC_79',  // Romanian default, can be overridden
        litter_capacity: 2,
        ambulatory_capacity: 4,
        armored: false
      },
      armored: {
        count: 1,
        type: 'M113_AMBULANCE',
        litter_capacity: 4,
        ambulatory_capacity: 4,
        armored: true
      }
    },
    helicopters: {
      count: 0,  // Helicopters are NOT organic to battalions
      note: 'MEDEVAC helicopters allocated at Division/Corps level'
    },
    personnel: {
      medics: 12,          // 4 per company + aid station
      drivers: 5,          // For ambulances
      litter_bearers: 8,   // For manual carry
      physician_assistant: 1,
      medical_officer: 0   // Role 1 typically PA-led
    },
    total_litter_capacity: 12,      // 4×2 + 1×4
    total_ambulatory_capacity: 20,  // 4×4 + 1×4
    sorties_per_day: 6              // Average ambulance runs
  },

  // Heavy/Armor battalion - more armored assets
  HEAVY: {
    ground_ambulances: {
      wheeled: {
        count: 2,
        type: 'HMMWV_AMBULANCE',
        litter_capacity: 4,
        ambulatory_capacity: 2,
        armored: true
      },
      armored: {
        count: 3,
        type: 'M113_AMBULANCE',
        litter_capacity: 4,
        ambulatory_capacity: 4,
        armored: true
      }
    },
    helicopters: {
      count: 0,
      note: 'MEDEVAC helicopters allocated at Division/Corps level'
    },
    personnel: {
      medics: 10,
      drivers: 5,
      litter_bearers: 6,
      physician_assistant: 1,
      medical_officer: 0
    },
    total_litter_capacity: 20,      // 2×4 + 3×4
    total_ambulatory_capacity: 16,  // 2×2 + 3×4
    sorties_per_day: 5              // Armored slower but more protected
  },

  // Artillery battalion - reduced medical footprint
  ARTILLERY: {
    ground_ambulances: {
      wheeled: {
        count: 2,
        type: 'TABC_79',
        litter_capacity: 2,
        ambulatory_capacity: 4,
        armored: false
      },
      armored: {
        count: 0,
        type: null,
        litter_capacity: 0,
        ambulatory_capacity: 0,
        armored: false
      }
    },
    helicopters: {
      count: 0,
      note: 'Artillery rear position - ground evac sufficient'
    },
    personnel: {
      medics: 6,
      drivers: 2,
      litter_bearers: 4,
      physician_assistant: 1,
      medical_officer: 0
    },
    total_litter_capacity: 4,
    total_ambulatory_capacity: 8,
    sorties_per_day: 4
  },

  // Light infantry (Airborne, Air Assault, Mountain)
  LIGHT: {
    ground_ambulances: {
      wheeled: {
        count: 3,
        type: 'HMMWV_AMBULANCE',
        litter_capacity: 4,
        ambulatory_capacity: 2,
        armored: false
      },
      armored: {
        count: 0,
        type: null,
        litter_capacity: 0,
        ambulatory_capacity: 0,
        armored: false
      }
    },
    helicopters: {
      count: 0,
      note: 'Light units rely heavily on MEDEVAC helicopter support'
    },
    personnel: {
      medics: 14,          // More medics to compensate for fewer vehicles
      drivers: 3,
      litter_bearers: 12,  // More manual carry capability
      physician_assistant: 1,
      medical_officer: 0
    },
    total_litter_capacity: 12,
    total_ambulatory_capacity: 6,
    sorties_per_day: 5
  }
};

// ============================================================================
// ROLE 2 BASIC (BRIGADE LEVEL) ORGANIC ASSETS BY PROFILE
// ============================================================================

/**
 * Role 2 Basic assets - Brigade Medical Company
 * Larger ambulance pool for collecting from subordinate Role 1s
 */
export const ROLE2_BASIC_ORGANIC_ASSETS = {
  // Standard brigade (Infantry, Mechanized)
  STANDARD: {
    ground_ambulances: {
      wheeled: {
        count: 8,
        type: 'TABC_79',
        litter_capacity: 2,
        ambulatory_capacity: 4,
        armored: false
      },
      armored: {
        count: 4,
        type: 'M113_AMBULANCE',
        litter_capacity: 4,
        ambulatory_capacity: 4,
        armored: true
      }
    },
    helicopters: {
      count: 0,  // Helicopters assigned at Division, not Brigade
      allocated_from_division: true,
      priority_request: true,
      note: 'Request MEDEVAC through Division CAB/Air Force'
    },
    personnel: {
      medics: 24,
      drivers: 12,
      litter_bearers: 16,
      physician_assistant: 2,
      medical_officer: 2,    // Role 2 has physicians
      nurses: 8,
      surgical_technicians: 0  // No surgery at Role 2B (unless FST attached)
    },
    total_litter_capacity: 32,      // 8×2 + 4×4
    total_ambulatory_capacity: 48,  // 8×4 + 4×4
    sorties_per_day: 8,
    collection_capacity_per_hour: 12  // Patients collected from Role 1s
  },

  // Heavy/Armor brigade
  HEAVY_BRIGADE: {
    ground_ambulances: {
      wheeled: {
        count: 4,
        type: 'HMMWV_AMBULANCE',
        litter_capacity: 4,
        ambulatory_capacity: 2,
        armored: true
      },
      armored: {
        count: 8,
        type: 'M113_AMBULANCE',
        litter_capacity: 4,
        ambulatory_capacity: 4,
        armored: true
      }
    },
    helicopters: {
      count: 0,
      allocated_from_division: true,
      priority_request: true,
      note: 'Armor operations prioritize armored ground evac'
    },
    personnel: {
      medics: 20,
      drivers: 12,
      litter_bearers: 12,
      physician_assistant: 2,
      medical_officer: 2,
      nurses: 8,
      surgical_technicians: 0
    },
    total_litter_capacity: 48,
    total_ambulatory_capacity: 40,
    sorties_per_day: 6,
    collection_capacity_per_hour: 10
  },

  // Light brigade (Airborne, Air Assault)
  LIGHT_BRIGADE: {
    ground_ambulances: {
      wheeled: {
        count: 6,
        type: 'HMMWV_AMBULANCE',
        litter_capacity: 4,
        ambulatory_capacity: 2,
        armored: false
      },
      armored: {
        count: 0,
        type: null,
        litter_capacity: 0,
        ambulatory_capacity: 0,
        armored: false
      }
    },
    helicopters: {
      count: 0,
      allocated_from_division: true,
      priority_request: true,  // Light units get helicopter priority
      note: 'Light brigades heavily dependent on MEDEVAC'
    },
    personnel: {
      medics: 28,
      drivers: 6,
      litter_bearers: 20,
      physician_assistant: 2,
      medical_officer: 2,
      nurses: 6,
      surgical_technicians: 0
    },
    total_litter_capacity: 24,
    total_ambulatory_capacity: 12,
    sorties_per_day: 5,
    collection_capacity_per_hour: 8
  },

  // Artillery brigade - minimal organic medical
  ARTILLERY_BRIGADE: {
    ground_ambulances: {
      wheeled: {
        count: 6,
        type: 'TABC_79',
        litter_capacity: 2,
        ambulatory_capacity: 4,
        armored: false
      },
      armored: {
        count: 2,
        type: 'M113_AMBULANCE',
        litter_capacity: 4,
        ambulatory_capacity: 4,
        armored: true
      }
    },
    helicopters: {
      count: 0,
      allocated_from_division: true,
      priority_request: false,  // Lower priority than maneuver
      note: 'Artillery casualties lower, ground evac usually sufficient'
    },
    personnel: {
      medics: 16,
      drivers: 8,
      litter_bearers: 8,
      physician_assistant: 2,
      medical_officer: 1,
      nurses: 4,
      surgical_technicians: 0
    },
    total_litter_capacity: 20,
    total_ambulatory_capacity: 32,
    sorties_per_day: 6,
    collection_capacity_per_hour: 8
  }
};

// ============================================================================
// ROLE 2 ENHANCED (DIVISION LEVEL) ORGANIC ASSETS
// ============================================================================

/**
 * Role 2 Enhanced - Division Field Hospital
 * Has MEDEVAC helicopters and significant ground assets
 */
export const ROLE2_ENHANCED_ORGANIC_ASSETS = {
  STANDARD: {
    ground_ambulances: {
      wheeled: {
        count: 12,
        type: 'TABC_79',
        litter_capacity: 2,
        ambulatory_capacity: 4,
        armored: false
      },
      armored: {
        count: 6,
        type: 'M113_AMBULANCE',
        litter_capacity: 4,
        ambulatory_capacity: 4,
        armored: true
      }
    },
    helicopters: {
      count: 4,  // Division has organic MEDEVAC
      type: 'UH60_BLACKHAWK',  // Or IAR330_PUMA for Romanian
      litter_capacity_per_aircraft: 4,
      ambulatory_capacity_per_aircraft: 6,
      range_km: 580,
      sorties_per_day_per_aircraft: 6,
      note: 'Organic to Division CAB, allocated to medical mission'
    },
    personnel: {
      medics: 40,
      drivers: 18,
      litter_bearers: 24,
      physician_assistant: 4,
      medical_officer: 8,
      nurses: 24,
      surgical_technicians: 12,
      flight_medics: 8  // For helicopter crews
    },
    total_litter_capacity: 48,        // Ground only
    total_ambulatory_capacity: 72,    // Ground only
    helicopter_litter_capacity: 16,   // 4 × 4
    helicopter_ambulatory_capacity: 24,
    sorties_per_day: 10,
    collection_capacity_per_hour: 20
  },

  // Romanian Division - uses Air Force MEDEVAC
  ROMANIAN: {
    ground_ambulances: {
      wheeled: {
        count: 10,
        type: 'TABC_79',
        litter_capacity: 2,
        ambulatory_capacity: 4,
        armored: false
      },
      armored: {
        count: 4,
        type: 'PIRANHA_AMBULANCE',
        litter_capacity: 4,
        ambulatory_capacity: 4,
        armored: true
      }
    },
    helicopters: {
      count: 0,  // Romanian uses Air Force, not organic
      allocated_from_air_force: true,
      request_process: 'AIR_FORCE_MEDEVAC_REQUEST',
      typical_response_time_min: 45,
      note: 'Request through Romanian Air Force, not Division organic'
    },
    personnel: {
      medics: 36,
      drivers: 14,
      litter_bearers: 20,
      physician_assistant: 3,
      medical_officer: 6,
      nurses: 20,
      surgical_technicians: 10,
      flight_medics: 0
    },
    total_litter_capacity: 36,
    total_ambulatory_capacity: 56,
    helicopter_litter_capacity: 0,  // Not organic
    helicopter_ambulatory_capacity: 0,
    sorties_per_day: 8,
    collection_capacity_per_hour: 16
  }
};

// ============================================================================
// ROLE 3 (CORPS LEVEL) ORGANIC ASSETS
// ============================================================================

/**
 * Role 3 - Combat Support Hospital
 * Significant transport assets for inter-theater and STRATEVAC
 */
export const ROLE3_ORGANIC_ASSETS = {
  STANDARD: {
    ground_ambulances: {
      wheeled: {
        count: 16,
        type: 'TABC_79',
        litter_capacity: 2,
        ambulatory_capacity: 4,
        armored: false
      },
      armored: {
        count: 8,
        type: 'M113_AMBULANCE',
        litter_capacity: 4,
        ambulatory_capacity: 4,
        armored: true
      },
      bus: {
        count: 4,
        type: 'MEDICAL_BUS',
        litter_capacity: 12,
        ambulatory_capacity: 24,
        armored: false,
        note: 'For mass casualty patient movement'
      }
    },
    helicopters: {
      count: 8,
      type: 'UH60_BLACKHAWK',
      litter_capacity_per_aircraft: 4,
      ambulatory_capacity_per_aircraft: 6,
      range_km: 580,
      sorties_per_day_per_aircraft: 6,
      note: 'Corps Aviation Brigade allocation'
    },
    fixed_wing: {
      count: 0,  // Fixed-wing is theater asset, not organic
      access_to_stratevac: true,
      stratevac_coordination: 'PATIENT_MOVEMENT_REQUEST',
      note: 'C-17/C-130 STRATEVAC coordinated through theater'
    },
    personnel: {
      medics: 60,
      drivers: 28,
      litter_bearers: 40,
      physician_assistant: 8,
      medical_officer: 20,
      nurses: 60,
      surgical_technicians: 24,
      flight_medics: 16,
      patient_admin: 12
    },
    total_litter_capacity: 112,       // Ground: 16×2 + 8×4 + 4×12
    total_ambulatory_capacity: 176,   // Ground: 16×4 + 8×4 + 4×24
    helicopter_litter_capacity: 32,   // 8 × 4
    helicopter_ambulatory_capacity: 48,
    sorties_per_day: 12,
    collection_capacity_per_hour: 30
  }
};

// ============================================================================
// EMPTY ASSET STRUCTURES (for initialization)
// ============================================================================

/**
 * Create empty asset block for attached/detached tracking
 * @returns {Object} Empty asset structure
 */
export const createEmptyAssetBlock = () => ({
  ground_ambulances: {
    wheeled: { count: 0 },
    armored: { count: 0 }
  },
  helicopters: { count: 0 },
  personnel: {
    medics: 0,
    drivers: 0,
    litter_bearers: 0
  },
  source_unit_id: null,
  destination_unit_id: null,
  transfer_date: null,
  duration: null,
  reason: null
});

/**
 * Create full facility asset structure with organic/attached/detached/effective
 * @param {Object} organicAssets - The organic assets for this facility
 * @returns {Object} Complete asset tracking structure
 */
export const createFacilityAssetStructure = (organicAssets) => ({
  // Permanently assigned to this unit (from TOE/MTOE)
  organic_assets: organicAssets || createEmptyAssetBlock(),
  
  // Temporarily received from another unit
  attached_assets: [],  // Array of asset blocks with source info
  
  // Temporarily sent to support another unit
  detached_assets: [],  // Array of asset blocks with destination info
  
  // Calculated: organic + attached - detached
  effective_assets: null,  // Calculated on demand
  
  // Metadata
  last_updated: new Date().toISOString(),
  asset_tracking_enabled: true
});

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get organic assets for Role 1 facility by unit type profile
 * @param {string} profile - Asset profile key (STANDARD, HEAVY, ARTILLERY, LIGHT)
 * @param {string} nationalStandard - National standard for equipment types
 * @returns {Object} Organic assets configuration
 */
export const getRole1OrganicAssets = (profile = 'STANDARD', nationalStandard = 'ROMANIAN') => {
  const assets = ROLE1_ORGANIC_ASSETS[profile] || ROLE1_ORGANIC_ASSETS.STANDARD;
  
  // Deep copy to avoid mutation
  const result = JSON.parse(JSON.stringify(assets));
  
  // Adjust equipment types by national standard
  if (nationalStandard === 'US') {
    result.ground_ambulances.wheeled.type = 'HMMWV_AMBULANCE';
  } else if (nationalStandard === 'NATO') {
    result.ground_ambulances.wheeled.type = 'NATO_AMBULANCE';
  }
  // Romanian keeps TABC_79 default
  
  return result;
};

/**
 * Get organic assets for Role 2 Basic facility by brigade type profile
 * @param {string} profile - Asset profile key
 * @param {string} nationalStandard - National standard
 * @returns {Object} Organic assets configuration
 */
export const getRole2BasicOrganicAssets = (profile = 'STANDARD', nationalStandard = 'ROMANIAN') => {
  const assets = ROLE2_BASIC_ORGANIC_ASSETS[profile] || ROLE2_BASIC_ORGANIC_ASSETS.STANDARD;
  
  const result = JSON.parse(JSON.stringify(assets));
  
  if (nationalStandard === 'US') {
    result.ground_ambulances.wheeled.type = 'HMMWV_AMBULANCE';
    result.ground_ambulances.armored.type = 'M113_AMBULANCE';
  }
  
  return result;
};

/**
 * Get organic assets for Role 2 Enhanced facility
 * @param {string} nationalStandard - National standard
 * @returns {Object} Organic assets configuration
 */
export const getRole2EnhancedOrganicAssets = (nationalStandard = 'ROMANIAN') => {
  if (nationalStandard === 'ROMANIAN') {
    return JSON.parse(JSON.stringify(ROLE2_ENHANCED_ORGANIC_ASSETS.ROMANIAN));
  }
  return JSON.parse(JSON.stringify(ROLE2_ENHANCED_ORGANIC_ASSETS.STANDARD));
};

/**
 * Get organic assets for Role 3 facility
 * @param {string} nationalStandard - National standard
 * @returns {Object} Organic assets configuration
 */
export const getRole3OrganicAssets = (nationalStandard = 'ROMANIAN') => {
  return JSON.parse(JSON.stringify(ROLE3_ORGANIC_ASSETS.STANDARD));
};

/**
 * Calculate effective assets (organic + attached - detached)
 * @param {Object} assetStructure - Facility asset structure with organic/attached/detached
 * @returns {Object} Effective assets available
 */
export const calculateEffectiveAssets = (assetStructure) => {
  if (!assetStructure || !assetStructure.organic_assets) {
    return createEmptyAssetBlock();
  }
  
  // Start with organic assets
  const effective = JSON.parse(JSON.stringify(assetStructure.organic_assets));
  
  // Add attached assets
  if (assetStructure.attached_assets && Array.isArray(assetStructure.attached_assets)) {
    assetStructure.attached_assets.forEach(attached => {
      if (attached.ground_ambulances) {
        effective.ground_ambulances.wheeled.count += attached.ground_ambulances.wheeled?.count || 0;
        effective.ground_ambulances.armored.count += attached.ground_ambulances.armored?.count || 0;
      }
      if (attached.helicopters) {
        effective.helicopters.count += attached.helicopters.count || 0;
      }
      if (attached.personnel) {
        effective.personnel.medics += attached.personnel.medics || 0;
        effective.personnel.drivers += attached.personnel.drivers || 0;
        effective.personnel.litter_bearers += attached.personnel.litter_bearers || 0;
      }
    });
  }
  
  // Subtract detached assets
  if (assetStructure.detached_assets && Array.isArray(assetStructure.detached_assets)) {
    assetStructure.detached_assets.forEach(detached => {
      if (detached.ground_ambulances) {
        effective.ground_ambulances.wheeled.count -= detached.ground_ambulances.wheeled?.count || 0;
        effective.ground_ambulances.armored.count -= detached.ground_ambulances.armored?.count || 0;
      }
      if (detached.helicopters) {
        effective.helicopters.count -= detached.helicopters.count || 0;
      }
      if (detached.personnel) {
        effective.personnel.medics -= detached.personnel.medics || 0;
        effective.personnel.drivers -= detached.personnel.drivers || 0;
        effective.personnel.litter_bearers -= detached.personnel.litter_bearers || 0;
      }
    });
  }
  
  // Ensure no negative values
  effective.ground_ambulances.wheeled.count = Math.max(0, effective.ground_ambulances.wheeled.count);
  effective.ground_ambulances.armored.count = Math.max(0, effective.ground_ambulances.armored.count);
  effective.helicopters.count = Math.max(0, effective.helicopters.count);
  effective.personnel.medics = Math.max(0, effective.personnel.medics);
  effective.personnel.drivers = Math.max(0, effective.personnel.drivers);
  effective.personnel.litter_bearers = Math.max(0, effective.personnel.litter_bearers);
  
  // Calculate totals
  const wheeledCap = effective.ground_ambulances.wheeled;
  const armoredCap = effective.ground_ambulances.armored;
  
  effective.total_litter_capacity = 
    (wheeledCap.count * (wheeledCap.litter_capacity || 2)) +
    (armoredCap.count * (armoredCap.litter_capacity || 4));
    
  effective.total_ambulatory_capacity = 
    (wheeledCap.count * (wheeledCap.ambulatory_capacity || 4)) +
    (armoredCap.count * (armoredCap.ambulatory_capacity || 4));
  
  return effective;
};

/**
 * Calculate daily evacuation capacity
 * @param {Object} effectiveAssets - Effective assets after attach/detach
 * @param {number} sortiesPerDay - Average sorties per ambulance per day
 * @returns {Object} Daily capacity breakdown
 */
export const calculateDailyEvacuationCapacity = (effectiveAssets, sortiesPerDay = 6) => {
  if (!effectiveAssets) {
    return { litter: 0, ambulatory: 0, total: 0 };
  }
  
  const groundLitter = effectiveAssets.total_litter_capacity * sortiesPerDay;
  const groundAmbulatory = effectiveAssets.total_ambulatory_capacity * sortiesPerDay;
  
  // Add helicopter capacity if available
  let heliLitter = 0;
  let heliAmbulatory = 0;
  
  if (effectiveAssets.helicopters && effectiveAssets.helicopters.count > 0) {
    const heliCount = effectiveAssets.helicopters.count;
    const litterPerHeli = effectiveAssets.helicopters.litter_capacity_per_aircraft || 4;
    const ambPerHeli = effectiveAssets.helicopters.ambulatory_capacity_per_aircraft || 6;
    const heliSorties = effectiveAssets.helicopters.sorties_per_day_per_aircraft || 6;
    
    heliLitter = heliCount * litterPerHeli * heliSorties;
    heliAmbulatory = heliCount * ambPerHeli * heliSorties;
  }
  
  return {
    ground: {
      litter: groundLitter,
      ambulatory: groundAmbulatory,
      total: groundLitter + groundAmbulatory
    },
    helicopter: {
      litter: heliLitter,
      ambulatory: heliAmbulatory,
      total: heliLitter + heliAmbulatory
    },
    combined: {
      litter: groundLitter + heliLitter,
      ambulatory: groundAmbulatory + heliAmbulatory,
      total: groundLitter + groundAmbulatory + heliLitter + heliAmbulatory
    }
  };
};

/**
 * Validate asset allocation for a facility
 * @param {Object} assetStructure - Facility asset structure
 * @returns {Object} Validation result with errors and warnings
 */
export const validateAssetAllocation = (assetStructure) => {
  const errors = [];
  const warnings = [];
  
  if (!assetStructure) {
    errors.push('Asset structure is missing');
    return { valid: false, errors, warnings };
  }
  
  if (!assetStructure.organic_assets) {
    errors.push('Organic assets not defined');
  }
  
  // Check for over-detachment
  const effective = calculateEffectiveAssets(assetStructure);
  
  if (effective.ground_ambulances.wheeled.count < 0) {
    errors.push('More wheeled ambulances detached than organic');
  }
  if (effective.ground_ambulances.armored.count < 0) {
    errors.push('More armored ambulances detached than organic');
  }
  if (effective.personnel.medics < 0) {
    errors.push('More medics detached than organic');
  }
  
  // Warnings for low capacity
  if (effective.ground_ambulances.wheeled.count === 0 && 
      effective.ground_ambulances.armored.count === 0) {
    warnings.push('No ground ambulances available - evacuation capacity severely limited');
  }
  
  if (effective.personnel.medics < 2) {
    warnings.push('Very few medics available - care quality may suffer');
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings,
    effective_assets: effective
  };
};

// ============================================================================
// EDUCATIONAL CONTENT
// ============================================================================

export const EVACUATION_ASSET_EDUCATIONAL = {
  key_principles: [
    'Organic assets are permanently assigned to a unit per TOE/MTOE',
    'Attached assets are temporarily received to augment capacity',
    'Detached assets are temporarily sent to support another unit',
    'Effective = Organic + Attached - Detached',
    'NATO PULL method: Higher echelon collects from lower',
    'PUSH method used in emergencies (MASCAL, route denial)'
  ],
  
  nato_doctrine: [
    'Role 1 relies on organic ground ambulances only',
    'Role 2 collects from Role 1 using brigade assets',
    'MEDEVAC helicopters are Division/Corps assets, not Brigade',
    'Romanian forces use Air Force MEDEVAC, not Army organic',
    'US forces have dedicated MEDEVAC units in Division CAB'
  ],
  
  ukraine_lessons: [
    'FPV drones target ambulances - armored variants essential',
    'MEDEVAC helicopter availability often <30% in contested airspace',
    'Ground evacuation times increased 2-3x due to route denial',
    'Cross-training drivers as medics proved critical',
    'Civilian vehicles often used when military assets destroyed'
  ],
  
  common_errors: [
    'Assuming helicopters are organic to brigades (they are not)',
    'Not accounting for maintenance downtime (15% unavailable)',
    'Ignoring driver fatigue limits (max 6 sorties/day)',
    'Planning with full TOE strength (reality is 70-85%)',
    'Forgetting armored ambulances for close combat units'
  ]
};

// ============================================================================
// OBS-S96-006: Unit type → ROLE1_ORGANIC_ASSETS profile mapping
// Used by computeEffectiveR1Fleet() in casualtyCalculator.js to derive
// the organic ambulance fleet from the ORBAT without manual input.
// ============================================================================

export const UNIT_TYPE_TO_ROLE1_PROFILE = {
  // Combat — standard (4 wheeled + 1 armoured per battalion)
  INFANTRY:        'STANDARD',
  MECHANIZED:      'STANDARD',
  STRYKER:         'STANDARD',
  // Armour — heavy (2 wheeled + 3 armoured per battalion)
  ARMOR:           'HEAVY',
  HEAVY:           'HEAVY',
  // Fire support / combat engineers (2 wheeled + 0 armoured)
  ARTILLERY:       'ARTILLERY',
  ENGINEERS:       'ARTILLERY',
  AIR_DEFENSE:     'ARTILLERY',
  // Light manoeuvre (3 wheeled + 0 armoured, rely on helo)
  AIRBORNE:        'LIGHT',
  AIR_ASSAULT:     'LIGHT',
  MOUNTAIN:        'LIGHT',
  RECONNAISSANCE:  'LIGHT',
  // CSS / support (minimal ground evac footprint)
  AVIATION:        'LIGHT',
  SIGNAL:          'LIGHT',
  LOGISTICS:       'LIGHT',
  SUPPLY:          'LIGHT',
  TRANSPORT:       'LIGHT',
  MAINTENANCE:     'LIGHT',
  MEDICAL:         'LIGHT',
  MEDICAL_SUPPORT: 'LIGHT',
  HQ:              'LIGHT',
  MILITARY_INTELLIGENCE: 'LIGHT',   // generated sub-unit of infantry brigades (2026-10-11)
  // 2026-10-24: the six defined functions (light kit, rely on the supported unit or helicopter)
  SPECIAL_FORCES:  'LIGHT',
  ATTACK_AVIATION: 'LIGHT',
  MILITARY_POLICE: 'LIGHT',
  CBRN_DEFENSE:    'LIGHT',
  CIVIL_AFFAIRS:   'LIGHT',
  PSYCHOLOGICAL_OPS: 'LIGHT',
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// Named default export (fixes lint warning)
const evacuationAssetAllocation = {
  ROLE1_ORGANIC_ASSETS,
  ROLE2_BASIC_ORGANIC_ASSETS,
  ROLE2_ENHANCED_ORGANIC_ASSETS,
  ROLE3_ORGANIC_ASSETS,
  createEmptyAssetBlock,
  createFacilityAssetStructure,
  getRole1OrganicAssets,
  getRole2BasicOrganicAssets,
  getRole2EnhancedOrganicAssets,
  getRole3OrganicAssets,
  calculateEffectiveAssets,
  calculateDailyEvacuationCapacity,
  validateAssetAllocation,
  EVACUATION_ASSET_EDUCATIONAL
};

export default evacuationAssetAllocation;
